import createSelector from 'selectorator'
import { identity } from 'ramda'
import { RootState } from 'src/state/types'
import { NAMESPACE } from './constants'
import {
  TAgencyAddress,
  TAgencyBankDetails,
  TAgencyContactDetails,
  TAgencyDetails,
  TAgencyEntity,
  TAgencyInvitationByToken,
  TAgencyListingItem,
  TAgencyProfile,
  TAgencySegment,
  TAgencyState,
  TAgencyTheme,
} from './types'

/**
 * Get a slice of the agency API state
 * EG: getSlice('byId')
 */
const getSlice = <K extends keyof TAgencyState>(key: K) =>
  createSelector<RootState, TAgencyState[K]>([`api.${NAMESPACE}.${key}`], identity)

export const agency = createSelector<RootState, TAgencyState>([`api.${NAMESPACE}`], identity)

export const byId = getSlice('byId')

export const allIds = getSlice('allIds')

export const invitations = getSlice('invitations')

export const getInvitationByToken = createSelector<RootState, (token?: string | null) => TAgencyInvitationByToken>(
  [getSlice('invitationsByToken')],
  (invitationsByToken: TAgencyState['invitationsByToken']) =>
    (token?: string | null): TAgencyInvitationByToken =>
      (token && invitationsByToken[token]) || {},
)

export const signupInvite = getSlice('signupInvite')

export const getThemes = getSlice('themes')

export const getAgencyById = createSelector<RootState, (id?: string | null) => TAgencyEntity | undefined>(
  [byId],
  (byId: TAgencyState['byId']) =>
    (id?: string | null): TAgencyEntity | undefined =>
      (id && byId[id]) || undefined,
)

/**
 * The agency the session is scoped to.
 *
 * Reads `api.user.currentAgencyId` as a path rather than calling
 * `userApiSelectors.getCurrentAgencyId`: the two modules used to import each
 * other's selectors, and a path dependency both breaks that cycle and keeps
 * the selector memoised on the two values it actually reads.
 */
export const getCurrentAgency = createSelector<RootState, TAgencyEntity | undefined>(
  [byId, 'api.user.currentAgencyId'],
  (byId: TAgencyState['byId'], currentAgencyId: string | null): TAgencyEntity | undefined =>
    (currentAgencyId && byId[currentAgencyId]) || undefined,
)

const getCurrentAgencyProfile = createSelector<RootState, TAgencyProfile>(
  [getCurrentAgency],
  (agency?: TAgencyEntity): TAgencyProfile => agency?.agency ?? {},
)

export const getCurrentAgencyDetails = createSelector<RootState, TAgencyDetails>(
  [getCurrentAgencyProfile],
  (profile: TAgencyProfile): TAgencyDetails => profile.agencyDetails ?? {},
)

export const getCurrentAgencyAddress = createSelector<RootState, TAgencyAddress>(
  [getCurrentAgencyProfile],
  (profile: TAgencyProfile): TAgencyAddress => profile.agencyAddress ?? {},
)

export const getCurrentAgencyBankDetails = createSelector<RootState, TAgencyBankDetails>(
  [getCurrentAgencyProfile],
  (profile: TAgencyProfile): TAgencyBankDetails => profile.agencyBankDetails ?? {},
)

export const getCurrentAgencyContactDetails = createSelector<RootState, TAgencyContactDetails>(
  [getCurrentAgencyProfile],
  (profile: TAgencyProfile): TAgencyContactDetails => profile.agencyContactDetails ?? {},
)

/** `false` rather than `undefined` so callers can `theme || fallbackTheme`. */
export const getCurrentAgencyTheme = createSelector<RootState, TAgencyTheme | false>(
  [getCurrentAgency],
  (agency?: TAgencyEntity): TAgencyTheme | false => agency?.theme ?? false,
)

export const getCurrentAgencyLogo = createSelector<RootState, string>(
  [getCurrentAgency],
  (agency?: TAgencyEntity): string => agency?.logoUrl ?? '',
)

export const getAgencyLogo = createSelector<RootState, (id?: string | null) => string>(
  [getAgencyById],
  (getAgencyById: (id?: string | null) => TAgencyEntity | undefined) =>
    (id?: string | null): string =>
      getAgencyById(id)?.logoUrl ?? '',
)

export const getAgencyTheme = createSelector<RootState, (id?: string | null) => TAgencyTheme | false>(
  [getAgencyById],
  (getAgencyById: (id?: string | null) => TAgencyEntity | undefined) =>
    (id?: string | null): TAgencyTheme | false =>
      getAgencyById(id)?.theme ?? false,
)

export const getAgencyName = createSelector<RootState, (id?: string | null) => string>(
  [getAgencyById],
  (getAgencyById: (id?: string | null) => TAgencyEntity | undefined) =>
    (id?: string | null): string =>
      getAgencyById(id)?.agency?.agencyDetails?.companyName ?? '',
)

export const getAgencyTradingName = createSelector<RootState, (id?: string | null) => string>(
  [getAgencyById],
  (getAgencyById: (id?: string | null) => TAgencyEntity | undefined) =>
    (id?: string | null): string =>
      getAgencyById(id)?.agency?.agencyDetails?.trading ?? '',
)

export const getCurrentAgencyName = createSelector<RootState, string>(
  [getCurrentAgencyDetails],
  (details: TAgencyDetails): string => details.companyName ?? '',
)

export const getAgencyTradingOrCompanyName = createSelector<RootState, (id?: string | null) => string>(
  [getAgencyById],
  (getAgencyById: (id?: string | null) => TAgencyEntity | undefined) =>
    (id?: string | null): string => {
      const details = getAgencyById(id)?.agency?.agencyDetails
      return details?.trading || details?.companyName || ''
    },
)

export const getCurrentAgencyGlobalVatEnabled = createSelector<RootState, boolean>(
  [getCurrentAgencyProfile],
  (profile: TAgencyProfile): boolean => profile.globalVatEnabled ?? false,
)

export const getCurrentAgencyIsTestAgency = createSelector<RootState, boolean>(
  [getCurrentAgencyProfile],
  (profile: TAgencyProfile): boolean => profile.isTestAgency ?? false,
)

export const getAgencyListing = getSlice('agencyListing')

export const getAgencyFromListingById = createSelector<
  RootState,
  (id?: string | null) => TAgencyListingItem | undefined
>(
  [getAgencyListing],
  (listing: TAgencyListingItem[]) =>
    (id?: string | null): TAgencyListingItem | undefined =>
      listing.find(({ agencyId }) => agencyId === id),
)

/**
 * @todo move to UI state module
 */
export const getAgencyForSearchResult = createSelector<
  RootState,
  (id?: string | null) => { id: string; text: string; partyTag: string }
>([getAgencyName], (getAgencyName: (id?: string | null) => string) => (id?: string | null) => ({
  id: id ?? '',
  text: getAgencyName(id),
  partyTag: 'agency',
}))

export const getInvitableParties = getSlice('invitableParties')

export const getSegments = getSlice('segments')

export const getSegmentById = createSelector<RootState, (id?: string | null) => TAgencySegment | undefined>(
  [getSegments],
  (segments: TAgencySegment[]) =>
    (id?: string | null): TAgencySegment | undefined =>
      segments.find(segment => segment.id === id),
)
